import { useEffect, useRef, useState } from "react";
import { VideoOff, X } from "lucide-react";
import {
  embedUrl,
  providerName,
  watchUrl,
  type ReferenceVideo,
} from "@/features/matches/referenceVideo";

/**
 * Full-screen player for a coach-attached reference video (#675).
 *
 * Playing inline was the original design and it did not work. The card lives
 * inside `FeedbackPlayerModal` — `max-h-[95vh]` with `overflow-y-auto` — so an
 * inline player competes with the coach's written feedback for scroll space.
 * Capping its height to keep the words visible letterboxed the video into a
 * ~10:1 strip; giving it a real 16:9 box pushed the writing off screen. There
 * is no height that satisfies both, because reading and watching want
 * different amounts of room.
 *
 * So the card stays a thumbnail — a citation you can tap — and playback takes
 * over the viewport here. Same on desktop and mobile: the difference is only
 * how much black surrounds the player.
 *
 * **The coach's "why this video" line stays visible under the player.** That
 * line is the entire reason the video was attached, and inline playback hid it
 * the moment the embed mounted. Keeping it on screen while watching is what
 * makes this a coaching player rather than a generic embed.
 *
 * Sits at `z-[160]`: above `FeedbackPlayerModal` (130) and the roster picker
 * (150), below the `z-[200]` dropdown layer.
 */
export function ReferenceVideoTheater({
  video,
  onClose,
  label = "Watch this",
}: {
  video: ReferenceVideo;
  onClose: () => void;
  label?: string;
}) {
  const closeRef = useRef<HTMLButtonElement | null>(null);
  // A direct file CAN report a load failure — unlike a cross-origin iframe,
  // whose `onError` never fires for a video that went private or 404'd. So
  // the `file` provider gets a real failure state; the embed providers rely
  // on the always-visible "Open it on …" link in the footer instead.
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    // The parent feedback modal also closes on Escape. Without
    // stopImmediatePropagation a single press closes BOTH, dumping the viewer
    // out of the feedback they were reading. Capture phase so this runs first.
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.stopImmediatePropagation();
      onClose();
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [onClose]);

  useEffect(() => {
    // Move focus into the theater so the close button is the first stop and
    // Escape has an obvious visible partner.
    closeRef.current?.focus();
    // Lock the page behind the overlay; a scrolling background under a
    // full-screen video reads as broken on a phone.
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);

  const isFile = video.provider === "file";

  return (
    <div
      data-testid="reference-video-theater"
      role="dialog"
      aria-modal="true"
      aria-label={video.title || label}
      onClick={onClose}
      // Pinned to the dark theme, which is what lets the chrome below use
      // semantic tokens on a fixed-black surface.
      //
      // The surface is always black, but the tokens INVERT per theme, so
      // neither side works alone. Measured against #000:
      //
      //              dark        light
      //   ink        19.11:1     1.27:1   <- invisible in light
      //   ink-muted   9.68:1     1.14:1   <- invisible in light
      //
      // Pinning resolves them to their dark values whatever the viewer's
      // preference, so `text-ink` stays at 19:1 instead of vanishing when
      // someone switches to light mode. `tokens.css` reserves dark for
      // "live video / telestrator surfaces" — this is one — and
      // `LandingPage` already scopes the theme to a subtree the same way.
      //
      // Fully opaque: a translucent backdrop let the feedback modal bleed
      // through behind the video and read as a rendering fault. The theater
      // is a mode change, not an overlay on the reading surface.
      data-theme="dark"
      className="fixed inset-0 z-[160] flex flex-col bg-black"
    >
      {/* Header: the label the coach gave this, and the way out. */}
      <div
        onClick={(e) => e.stopPropagation()}
        className="flex items-start justify-between gap-3 px-4 py-3 sm:px-6"
      >
        <div className="min-w-0">
          <p className="text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
            {label}
          </p>
          {video.title ? (
            <p className="truncate text-sm font-semibold text-ink sm:text-base">
              {video.title}
            </p>
          ) : null}
        </div>
        <button
          ref={closeRef}
          type="button"
          onClick={onClose}
          data-testid="reference-video-theater-close"
          aria-label="Close video"
          // 44px minimum touch target (Apple HIG), and the only control that
          // has to be reachable one-handed on a phone.
          //
          // `text-ink` rather than `text-ink-muted`: this is the sole way out
          // of a full-screen takeover, so it should not be the dimmest thing
          // on screen. Hover uses `bg-elevated` — `border-rule` measures
          // 1.25:1 on black even in the dark theme, so a hairline border
          // would be invisible here.
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-pill text-ink hover:bg-elevated focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
        >
          <X aria-hidden className="h-5 w-5" />
        </button>
      </div>

      {/* The player. `aspect-video` inside a flex-1 box keeps the provider's
        * own shape at every viewport — the letterboxing bug was a fixed
        * height applied to a 16:9 medium. */}
      <div
        onClick={(e) => e.stopPropagation()}
        className="flex min-h-0 flex-1 items-center justify-center sm:px-6"
      >
        {/* `w-full` so a phone fills its width — the video is the content, and
          * dead margins waste the room the theater exists to provide. On
          * desktop `max-w-5xl` left 128px gutters either side, which read as
          * wasted space in a view whose whole job is watching; `max-w-7xl`
          * uses the screen while `max-h-full` still keeps a short or landscape
          * viewport from overflowing. */}
        <div className="relative aspect-video max-h-full w-full max-w-7xl bg-black">
          {failed ? (
            // The file is gone, moved, or the host is unreachable. Say so
            // instead of leaving a stalled black rectangle — and keep the
            // coach's title, so the viewer still knows what they were meant
            // to watch and can ask about it.
            <div
              data-testid="reference-video-theater-unavailable"
              className="absolute inset-0 flex flex-col items-center justify-center gap-2 px-6 text-center"
            >
              <VideoOff aria-hidden className="h-6 w-6 text-ink-muted" />
              <p className="text-sm text-ink">
                This video couldn&rsquo;t be played.
              </p>
              {video.title ? (
                <p className="text-xs text-ink-muted">{video.title}</p>
              ) : null}
              <p className="text-xs text-ink-muted">
                It may have been moved or deleted. Let your coach know.
              </p>
            </div>
          ) : isFile ? (
            <video
              src={embedUrl(video)}
              controls
              autoPlay
              playsInline
              data-testid="reference-video-file-player"
              className="absolute inset-0 h-full w-full"
              // A direct file reports its own load failures, so this fires
              // for real (a 404, a moved file, an unreachable host). The
              // iframe path has no equivalent — a cross-origin frame never
              // reports what happened inside it.
              onError={() => setFailed(true)}
            />
          ) : (
            <iframe
              src={embedUrl(video)}
              title={video.title || label}
              data-testid="reference-video-embed"
              // `allow-same-origin` is REQUIRED for playback. Without it the
              // provider's script loads but never initialises — no player, no
              // <video>, just a black box. That shipped, and is what "the
              // video doesn't play" was.
              //
              // It grants the frame access to ITS OWN origin
              // (youtube-nocookie.com), not to ours. The dangerous pairing is
              // allow-scripts + allow-same-origin on a SAME-origin frame,
              // which lets it strip its own sandbox; this frame is
              // cross-origin, so that escape isn't available.
              sandbox="allow-scripts allow-same-origin allow-presentation"
              allow="autoplay; fullscreen; encrypted-media; picture-in-picture"
              referrerPolicy="strict-origin-when-cross-origin"
              className="absolute inset-0 h-full w-full border-0"
            />
          )}
        </div>
      </div>

      {/* The coach's reason, and the escape hatch for a dead embed. `mt-auto`
        * pins this to the bottom so the player stays optically centred rather
        * than being pushed up by a long label. */}
      <div
        onClick={(e) => e.stopPropagation()}
        className="mt-auto px-4 pb-5 pt-3 sm:px-6"
      >
        {video.label ? (
          <p
            data-testid="reference-video-theater-label"
            className="mx-auto max-w-5xl text-sm text-ink"
          >
            {video.label}
          </p>
        ) : null}
        {/* `text-ink-muted` measures 9.68:1 on black; the raw 40%-opacity
          * value this replaced measured 3.66:1 — large-text-only, and the
          * dimmest thing on screen. This is the privacy disclosure telling a
          * family nothing reaches the provider until they press play, so it
          * should not have been the hardest line to read. */}
        {/* Dropped entirely once a file has failed: "Playing in tktic's own
          * player" would be untrue, and the third-party line's "if it
          * doesn't play" hedge is redundant beside a panel already saying it
          * didn't. The embed providers keep their line either way — their
          * failures are invisible to us, so the link out is all we have. */}
        {failed && isFile ? null : (
          <p className="mx-auto mt-1 max-w-5xl text-xs text-ink-muted">
            {isFile ? (
              "Playing in tktic's own player."
            ) : (
              <>
                Playing from {providerName(video.provider)}.{" "}
                <a
                  href={watchUrl(video)}
                  target="_blank"
                  rel="noreferrer noopener"
                  data-testid="reference-video-theater-source"
                  className="text-accent underline decoration-accent/40 underline-offset-2 hover:decoration-accent"
                >
                  Open it on {providerName(video.provider)}
                </a>{" "}
                if it doesn&rsquo;t play.
              </>
            )}
          </p>
        )}
      </div>
    </div>
  );
}
